/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import {
  AgendaService,
  ActivityPlan,
  Department,
  User,
  MaintenanceUnit,
  AreaGroup,
  AreaSubgroup,
  Area,
  EquipmentType,
  Equipment,
} from 'services/reports';
import { AgendaTask } from '../utils/eventUtils';
import { useHeaderFilters } from 'context/GlobalFiltersContext';
import { AuthContext } from 'context/AuthContext';
import { parseJwt } from 'utils/parse';
import { EventsByDate, transformAgendaTasksToEvents } from '../utils/eventUtils';
import { buildApiPayload } from '../utils/payloadUtils';
import { startOfWeek } from 'date-fns';
import { CalendarView } from './useCalendarNavigation';
export type EventType = 'pendentes' | 'baixadas';

interface FilterOption {
  label: string;
  value: string;
  foto?: string;
}

interface CalendarFields {
  planos: FilterOption[];
  executores: FilterOption[];
  setores: FilterOption[];
}

interface UseCalendarFiltersParams {
  currentDate: Date;
  view: CalendarView;
}

interface UseCalendarFiltersReturn {
  type: EventType;
  setType: (type: EventType) => void;
  planos: FilterOption[];
  setPlanos: (planos: FilterOption[]) => void;
  planosSelectionLabel: string;
  planosValues: string[];
  handlePlanosChange: (values: unknown) => void;
  planosOptions: FilterOption[];
  ocorrencias: FilterOption[];
  setOcorrencias: (ocorrencias: FilterOption[]) => void;
  executores: FilterOption[];
  setExecutores: (executores: FilterOption[] | undefined) => void;
  executoresValues: string[];
  handleExecutoresChange: (values: unknown) => void;
  executoresOptions: FilterOption[];
  tempExecutoresValues: string[];
  handleTempExecutoresChange: (values: unknown) => void;
  setores: FilterOption[];
  setSetores: (setores: FilterOption[] | undefined) => void;
  setoresValues: string[];
  handleSetoresChange: (values: unknown) => void;
  setoresOptions: FilterOption[];
  tempSetoresValues: string[];
  handleTempSetoresChange: (values: unknown) => void;
  applyExecutoresSetoresFilters: () => void;
  clearExecutoresSetoresFilters: () => void;
  fields: CalendarFields;
  openFilters: boolean;
  setOpenFilters: (open: boolean) => void;
  executoresSetoresModalOpen: boolean;
  setExecutoresSetoresModalOpen: (open: boolean) => void;
  ambientesEquipamentosModalOpen: boolean;
  setAmbientesEquipamentosModalOpen: (open: boolean) => void;
  planosModalOpen: boolean;
  setPlanosModalOpen: (open: boolean) => void;
  tempPlanosValues: string[];
  handleTempPlanosChange: (values: unknown) => void;
  tempMaintenanceUnitsForPlanosValues: string[];
  handleTempMaintenanceUnitsForPlanosChange: (values: unknown) => void;
  applyPlanosFilters: () => void;
  clearPlanosFilters: () => void;
  maintenanceUnitsOptions: FilterOption[];
  maintenanceUnitsValues: string[];
  handleMaintenanceUnitsChange: (values: unknown) => void;
  tempMaintenanceUnitsValues: string[];
  handleTempMaintenanceUnitsChange: (values: unknown) => void;
  areaGroupsOptions: FilterOption[];
  areaGroupsValues: string[];
  handleAreaGroupsChange: (values: unknown) => void;
  tempAreaGroupsValues: string[];
  handleTempAreaGroupsChange: (values: unknown) => void;
  areaSubgroupsOptions: FilterOption[];
  areaSubgroupsValues: string[];
  handleAreaSubgroupsChange: (values: unknown) => void;
  tempAreaSubgroupsValues: string[];
  handleTempAreaSubgroupsChange: (values: unknown) => void;
  areasOptions: FilterOption[];
  areasValues: string[];
  handleAreasChange: (values: unknown) => void;
  tempAreasValues: string[];
  handleTempAreasChange: (values: unknown) => void;
  equipmentTypesOptions: FilterOption[];
  equipmentTypesValues: string[];
  handleEquipmentTypesChange: (values: unknown) => void;
  tempEquipmentTypesValues: string[];
  handleTempEquipmentTypesChange: (values: unknown) => void;
  equipmentOptions: FilterOption[];
  equipmentValues: string[];
  handleEquipmentChange: (values: unknown) => void;
  tempEquipmentValues: string[];
  handleTempEquipmentChange: (values: unknown) => void;
  applyAmbientesEquipamentosFilters: () => void;
  clearAmbientesEquipamentosFilters: () => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  events: EventsByDate;
  isLoadingEvents: boolean;
  fetchAgendaTasks: (isWeekView?: boolean, overrides?: { planosValues?: string[] }) => Promise<void>;
  setWeekView: (isWeekView: boolean) => void;
  loadMoreAreas: () => Promise<void>;
  searchArea: (term: string) => void;
  loadMoreEquipamentos: () => Promise<void>;
  searchEquipamento: (term: string) => void;
}

export const useCalendarFilters = (params?: UseCalendarFiltersParams): UseCalendarFiltersReturn => {
  const { currentDate = new Date(), view } = params || {};
  const { authContext } = useContext(AuthContext);

  const effectiveDate = useMemo(() => {
    if (!currentDate) return startOfWeek(new Date(), { weekStartsOn: 0 });
    if (view === undefined || view === CalendarView.Semana) {
      return startOfWeek(currentDate, { weekStartsOn: 0 });
    }
    return currentDate;
  }, [currentDate, view]);
  const [type, setType] = useState<EventType>('pendentes');
  const [planos, setPlanos] = useState<FilterOption[]>([]);
  const [planosOptions, setPlanosOptions] = useState<FilterOption[]>([]);
  const [ocorrencias, setOcorrencias] = useState<FilterOption[]>([]);
  const [executores, setExecutoresState] = useState<FilterOption[]>([]);
  const [executoresOptions, setExecutoresOptions] = useState<FilterOption[]>([]);
  const [tempExecutores, setTempExecutores] = useState<FilterOption[]>([]);
  const [setores, setSetoresState] = useState<FilterOption[]>([]);
  const [setoresOptions, setSetoresOptions] = useState<FilterOption[]>([]);
  const [tempSetores, setTempSetores] = useState<FilterOption[]>([]);
  const [openFilters, setOpenFilters] = useState<boolean>(true);
  const [executoresSetoresModalOpen, setExecutoresSetoresModalOpen] = useState<boolean>(false);
  const [ambientesEquipamentosModalOpen, setAmbientesEquipamentosModalOpen] = useState<boolean>(false);
  const [planosModalOpen, setPlanosModalOpen] = useState<boolean>(false);
  const [tempPlanos, setTempPlanos] = useState<FilterOption[]>([]);
  const [tempMaintenanceUnitsForPlanos, setTempMaintenanceUnitsForPlanos] = useState<FilterOption[]>([]);
  const [appliedMaintenanceUnitsForPlanos, setAppliedMaintenanceUnitsForPlanos] = useState<FilterOption[]>([]);
  const [maintenanceUnitsOptions, setMaintenanceUnitsOptions] = useState<FilterOption[]>([]);
  const [maintenanceUnits, setMaintenanceUnits] = useState<FilterOption[]>([]);
  const [tempMaintenanceUnits, setTempMaintenanceUnits] = useState<FilterOption[]>([]);
  const [areaGroupsOptions, setAreaGroupsOptions] = useState<FilterOption[]>([]);
  const [areaGroups, setAreaGroups] = useState<FilterOption[]>([]);
  const [tempAreaGroups, setTempAreaGroups] = useState<FilterOption[]>([]);
  const [areaSubgroupsOptions, setAreaSubgroupsOptions] = useState<FilterOption[]>([]);
  const [areaSubgroups, setAreaSubgroups] = useState<FilterOption[]>([]);
  const [tempAreaSubgroups, setTempAreaSubgroups] = useState<FilterOption[]>([]);
  const [areasOptions, setAreasOptions] = useState<FilterOption[]>([]);
  const [areas, setAreas] = useState<FilterOption[]>([]);
  const [tempAreas, setTempAreas] = useState<FilterOption[]>([]);
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<FilterOption[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<FilterOption[]>([]);
  const [tempEquipmentTypes, setTempEquipmentTypes] = useState<FilterOption[]>([]);
  const equipmentTypesDataRef = useRef<EquipmentType[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<FilterOption[]>([]);
  const [equipment, setEquipment] = useState<FilterOption[]>([]);
  const [tempEquipment, setTempEquipment] = useState<FilterOption[]>([]);
  const [events, setEvents] = useState<EventsByDate>({});
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);
  const isWeekViewRef = useRef<boolean>(true);
  const lastFetchParamsRef = useRef<string>('');
  const isMountedRef = useRef<boolean>(true);
  const [areaPage, setAreaPage] = useState<number>(1);
  const [areaSearch, setAreaSearch] = useState<string>('');
  const [equipamentoPage, setEquipamentoPage] = useState<number>(1);
  const [equipamentoSearch, setEquipamentoSearch] = useState<string>('');
  const debounceAreaRef = useRef<NodeJS.Timeout | null>(null);
  const debounceEquipamentoRef = useRef<NodeJS.Timeout | null>(null);

  const { headerFiltersContext } = useHeaderFilters();
  const { headerFilters } = headerFiltersContext;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const currentDateString = useMemo(() => {
    if (!effectiveDate) return undefined;
    const year = effectiveDate.getFullYear();
    const month = String(effectiveDate.getMonth() + 1).padStart(2, '0');
    const day = String(effectiveDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [effectiveDate?.getTime()]);

  const hasActiveFilters = useMemo(
    () =>
      planos.length > 0 ||
      executores.length > 0 ||
      setores.length > 0 ||
      maintenanceUnits.length > 0 ||
      areaGroups.length > 0 ||
      areaSubgroups.length > 0 ||
      areas.length > 0 ||
      equipmentTypes.length > 0 ||
      equipment.length > 0 ||
      type !== 'pendentes',
    [planos, executores, setores, maintenanceUnits, areaGroups, areaSubgroups, areas, equipmentTypes, equipment, type],
  );

  const fields = useMemo<CalendarFields>(
    () => ({
      planos: planosOptions,
      executores: executoresOptions,
      setores: setoresOptions,
    }),
    [planosOptions, executoresOptions, setoresOptions],
  );

  const planosSelectionLabel = useMemo(
    () => (planos.length > 0 ? ` (${planos.length} selecionado${planos.length > 1 ? 's' : ''})` : ''),
    [planos.length],
  );
  const planosValues = useMemo(() => planos.map(opt => opt.value), [planos]);
  const executoresValues = useMemo(() => (executores || []).map(opt => opt.value), [executores]);
  const tempExecutoresValues = useMemo(() => tempExecutores.map(opt => opt.value), [tempExecutores]);
  const setoresValues = useMemo(() => (setores || []).map(opt => opt.value), [setores]);
  const tempSetoresValues = useMemo(() => tempSetores.map(opt => opt.value), [tempSetores]);
  const maintenanceUnitsValues = useMemo(() => maintenanceUnits.map(opt => opt.value), [maintenanceUnits]);
  const tempMaintenanceUnitsValues = useMemo(() => tempMaintenanceUnits.map(opt => opt.value), [tempMaintenanceUnits]);
  const tempPlanosValues = useMemo(() => tempPlanos.map(opt => opt.value), [tempPlanos]);
  const tempMaintenanceUnitsForPlanosValues = useMemo(
    () => tempMaintenanceUnitsForPlanos.map(opt => opt.value),
    [tempMaintenanceUnitsForPlanos],
  );
  const areaGroupsValues = useMemo(() => areaGroups.map(opt => opt.value), [areaGroups]);
  const tempAreaGroupsValues = useMemo(() => tempAreaGroups.map(opt => opt.value), [tempAreaGroups]);
  const areaSubgroupsValues = useMemo(() => areaSubgroups.map(opt => opt.value), [areaSubgroups]);
  const tempAreaSubgroupsValues = useMemo(() => tempAreaSubgroups.map(opt => opt.value), [tempAreaSubgroups]);
  const areasValues = useMemo(() => areas.map(opt => opt.value), [areas]);
  const tempAreasValues = useMemo(() => tempAreas.map(opt => opt.value), [tempAreas]);
  const equipmentTypesValues = useMemo(() => equipmentTypes.map(opt => opt.value), [equipmentTypes]);
  const tempEquipmentTypesValues = useMemo(() => tempEquipmentTypes.map(opt => opt.value), [tempEquipmentTypes]);
  const equipmentValues = useMemo(() => equipment.map(opt => opt.value), [equipment]);
  const tempEquipmentValues = useMemo(() => tempEquipment.map(opt => opt.value), [tempEquipment]);
  const allMaintenanceUnitsValues = useMemo(() => {
    const localValues = maintenanceUnitsValues;
    const globalUnitId = headerFilters?.units?.id ? String(headerFilters.units.id) : null;
    if (localValues.length === 0 && globalUnitId) {
      return [globalUnitId];
    }
    if (globalUnitId && !localValues.includes(globalUnitId)) {
      return [...localValues, globalUnitId];
    }
    return localValues;
  }, [maintenanceUnitsValues, headerFilters?.units?.id]);
  const globalUnitGroupValues = useMemo(() => {
    const globalUnitGroupId = headerFilters?.unitGroups?.id ? String(headerFilters.unitGroups.id) : null;
    return globalUnitGroupId ? [globalUnitGroupId] : [];
  }, [headerFilters?.unitGroups?.id]);
  const globalUnitSubgroupValues = useMemo(() => {
    const globalUnitSubgroupId = headerFilters?.unitSubgroups?.id ? String(headerFilters.unitSubgroups.id) : null;
    return globalUnitSubgroupId ? [globalUnitSubgroupId] : [];
  }, [headerFilters?.unitSubgroups?.id]);
  const globalStateValues = useMemo(() => {
    const globalStateId = headerFilters?.states?.id ? String(headerFilters.states.id) : null;
    return globalStateId ? [globalStateId] : [];
  }, [headerFilters?.states?.id]);
  const globalCityValues = useMemo(() => {
    const globalCityId = headerFilters?.cities?.id ? String(headerFilters.cities.id) : null;
    return globalCityId ? [globalCityId] : [];
  }, [headerFilters?.cities?.id]);

  const globalSystemValues = useMemo(() => {
    const globalSystemId = headerFilters?.systems?.id ? String(headerFilters.systems.id) : null;
    return globalSystemId ? [globalSystemId] : [];
  }, [headerFilters?.systems?.id]);

  const setExecutores = useCallback((executores: FilterOption[] | undefined) => {
    setExecutoresState(executores || []);
  }, []);

  const setSetores = useCallback((setores: FilterOption[] | undefined) => {
    setSetoresState(setores || []);
  }, []);

  const handlePlanosChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = planosOptions.filter(opt => stringValues.includes(opt.value));
      setPlanos(selectedOptions);
    },
    [planosOptions],
  );

  const handleExecutoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = executoresOptions.filter(opt => stringValues.includes(opt.value));
      setExecutores(selectedOptions.length > 0 ? selectedOptions : undefined);
    },
    [executoresOptions, setExecutores],
  );

  const handleTempExecutoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = executoresOptions.filter(opt => stringValues.includes(opt.value));
      setTempExecutores(selectedOptions);
    },
    [executoresOptions],
  );

  const handleSetoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = setoresOptions.filter(opt => stringValues.includes(opt.value));
      setSetores(selectedOptions.length > 0 ? selectedOptions : undefined);
    },
    [setoresOptions, setSetores],
  );

  const handleTempSetoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = setoresOptions.filter(opt => stringValues.includes(opt.value));
      setTempSetores(selectedOptions);
    },
    [setoresOptions],
  );

  const applyExecutoresSetoresFilters = useCallback(() => {
    setExecutores(tempExecutores.length > 0 ? tempExecutores : undefined);
    setSetores(tempSetores.length > 0 ? tempSetores : undefined);
    setExecutoresSetoresModalOpen(false);
  }, [tempExecutores, tempSetores]);

  const clearExecutoresSetoresFilters = useCallback(() => {
    setTempExecutores([]);
    setTempSetores([]);
    setExecutores([]);
    setSetores([]);
    setExecutoresSetoresModalOpen(false);
  }, []);

  const handleTempPlanosChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = planosOptions.filter(opt => stringValues.includes(opt.value));
      setTempPlanos(selectedOptions);
    },
    [planosOptions],
  );

  const handleTempMaintenanceUnitsForPlanosChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = maintenanceUnitsOptions.filter(opt => stringValues.includes(opt.value));
      setTempMaintenanceUnitsForPlanos(selectedOptions);
    },
    [maintenanceUnitsOptions],
  );

  const applyPlanosFilters = useCallback(() => {
    setPlanos(tempPlanos);
    setAppliedMaintenanceUnitsForPlanos(tempMaintenanceUnitsForPlanos);
    setPlanosModalOpen(false);
  }, [tempPlanos, tempMaintenanceUnitsForPlanos]);

  const clearPlanosFilters = useCallback(() => {
    setTempPlanos([]);
    setTempMaintenanceUnitsForPlanos([]);
    setAppliedMaintenanceUnitsForPlanos([]);
    setPlanos([]);
    setPlanosModalOpen(false);
  }, []);

  const handleMaintenanceUnitsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = maintenanceUnitsOptions.filter(opt => stringValues.includes(opt.value));
      setMaintenanceUnits(selectedOptions);
    },
    [maintenanceUnitsOptions],
  );

  const handleAreaGroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaGroupsOptions.filter(opt => stringValues.includes(opt.value));
      setAreaGroups(selectedOptions);
    },
    [areaGroupsOptions],
  );

  const handleAreaSubgroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaSubgroupsOptions.filter(opt => stringValues.includes(opt.value));
      setAreaSubgroups(selectedOptions);
    },
    [areaSubgroupsOptions],
  );

  const handleAreasChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areasOptions.filter(opt => stringValues.includes(opt.value));
      setAreas(selectedOptions);
    },
    [areasOptions],
  );

  const handleEquipmentTypesChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentTypesOptions.filter(opt => stringValues.includes(opt.value));
      setEquipmentTypes(selectedOptions);
    },
    [equipmentTypesOptions],
  );

  const handleEquipmentChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentOptions.filter(opt => stringValues.includes(opt.value));
      setEquipment(selectedOptions);
    },
    [equipmentOptions],
  );

  const handleTempMaintenanceUnitsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = maintenanceUnitsOptions.filter(opt => stringValues.includes(opt.value));
      setTempMaintenanceUnits(selectedOptions);
    },
    [maintenanceUnitsOptions],
  );

  const handleTempAreaGroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaGroupsOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreaGroups(selectedOptions);
    },
    [areaGroupsOptions],
  );

  const handleTempAreaSubgroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaSubgroupsOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreaSubgroups(selectedOptions);
    },
    [areaSubgroupsOptions],
  );

  const handleTempAreasChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areasOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreas(selectedOptions);
    },
    [areasOptions],
  );

  const handleTempEquipmentTypesChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentTypesOptions.filter(opt => stringValues.includes(opt.value));
      setTempEquipmentTypes(selectedOptions);
    },
    [equipmentTypesOptions],
  );

  const handleTempEquipmentChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentOptions.filter(opt => stringValues.includes(opt.value));
      setTempEquipment(selectedOptions);
    },
    [equipmentOptions],
  );

  const applyAmbientesEquipamentosFilters = useCallback(() => {
    setMaintenanceUnits(tempMaintenanceUnits);
    setAreaGroups(tempAreaGroups);
    setAreaSubgroups(tempAreaSubgroups);
    setAreas(tempAreas);
    setEquipmentTypes(tempEquipmentTypes);
    setEquipment(tempEquipment);
    setAmbientesEquipamentosModalOpen(false);
  }, [tempMaintenanceUnits, tempAreaGroups, tempAreaSubgroups, tempAreas, tempEquipmentTypes, tempEquipment]);

  const clearAmbientesEquipamentosFilters = useCallback(() => {
    setTempMaintenanceUnits([]);
    setTempAreaGroups([]);
    setTempAreaSubgroups([]);
    setTempAreas([]);
    setTempEquipmentTypes([]);
    setTempEquipment([]);
    setMaintenanceUnits([]);
    setAreaGroups([]);
    setAreaSubgroups([]);
    setAreas([]);
    setEquipmentTypes([]);
    setEquipment([]);
    setAmbientesEquipamentosModalOpen(false);
  }, []);

  const clearAllFilters = useCallback(() => {
    setPlanos([]);
    setOcorrencias([]);
    setExecutoresState([]);
    setSetoresState([]);
    setMaintenanceUnits([]);
    setAreaGroups([]);
    setAreaSubgroups([]);
    setAreas([]);
    setEquipmentTypes([]);
    setEquipment([]);
    setType('pendentes');
  }, []);

  const fetchActivitiesPlans = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId =
      planosModalOpen && tempMaintenanceUnitsForPlanos.length > 0
        ? tempMaintenanceUnitsForPlanos[0].value
        : headerFilters?.units?.id;
    const systemId = headerFilters?.systems?.id;

    if (!companyId || !unitId) {
      setPlanosOptions([]);
      return;
    }

    const companyIdNum = Number(companyId);
    const unitIdNum = Number(unitId);
    const systemIdNum = systemId ? Number(systemId) : null;
    const systemIds = systemIdNum ? [systemIdNum] : undefined;

    try {
      const response = await AgendaService.getActivitiesPlans({
        companyId: companyIdNum,
        unitId: unitIdNum,
        systemIds: systemIds,
      });

      if (!response?.data) {
        setPlanosOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as ActivityPlan[]).map(item => ({
        label: item.nome,
        value: String(item.planoId),
      }));

      setPlanosOptions(options);
    } catch (error) {
      console.error('Erro ao buscar planos de atividades:', error);
      setPlanosOptions([]);
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.systems?.id,
    planosModalOpen,
    tempMaintenanceUnitsForPlanos,
  ]);

  const fetchSetores = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId || !unitId) {
      setSetoresOptions([]);
      return;
    }

    try {
      const response = await AgendaService.getCompanyDepartments({
        companyId: Number(companyId),
        unitId: Number(unitId),
      });

      if (!response?.data) {
        setSetoresOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as Department[]).map(item => ({
        label: item.nome,
        value: String(item.setor),
      }));

      setSetoresOptions(options);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
      setSetoresOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id]);

  const fetchExecutores = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId || !unitId) {
      setExecutoresOptions([]);
      return;
    }

    const siteId = unitId;

    try {
      const response = await AgendaService.getUsers({
        siteId: Number(siteId),
        companyId: Number(companyId),
        unitId: Number(unitId),
      });

      if (!response?.data) {
        setExecutoresOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as User[]).map(item => {
        let fotoUrl: string | undefined;
        if (item.image) {
          if (item.image.startsWith('http://') || item.image.startsWith('https://')) {
            fotoUrl = item.image;
          } else {
            const baseUrl = process.env.REACT_APP_URL_IMAGES || '';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            const cleanPath = item.image.startsWith('/') ? item.image : `/${item.image}`;
            const url = `${cleanBaseUrl}${cleanPath}`;
            if (url && url !== '/') {
              fotoUrl = url;
            }
          }
        }
        return {
          label: item.nome,
          value: String(item.usuario),
          foto: fotoUrl ?? '',
        };
      });

      setExecutoresOptions(options);
    } catch (error) {
      console.error('Erro ao buscar executores:', error);
      setExecutoresOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id]);

  const fetchMaintenanceUnits = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setMaintenanceUnitsOptions([]);
      return;
    }

    try {
      const response = await AgendaService.getMaintenanceUnits({
        companyId: Number(companyId),
        unitId: unitId ? Number(unitId) : undefined,
      });

      if (!response?.data) {
        setMaintenanceUnitsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as MaintenanceUnit[]).map(item => ({
        label: item.nome,
        value: String(item.site),
      }));

      setMaintenanceUnitsOptions(options);

      // Pré-seleciona se houver filtro global de units
      const globalUnitId = headerFilters?.units?.id;
      if (globalUnitId) {
        const globalUnitOption = options.find(opt => opt.value === String(globalUnitId));
        if (globalUnitOption && !maintenanceUnits.find(opt => opt.value === String(globalUnitId))) {
          setMaintenanceUnits([globalUnitOption]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar unidades de manutenção:', error);
      setMaintenanceUnitsOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id, maintenanceUnits]);

  const fetchAreaGroups = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setAreaGroupsOptions([]);
      return;
    }

    try {
      const response = await AgendaService.getAreaGroups({
        empresaId: Number(companyId),
        unidadeId: unitId ? Number(unitId) : undefined,
        companyId: Number(companyId),
        unitId: unitId ? Number(unitId) : undefined,
      });

      if (!response?.data) {
        setAreaGroupsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as AreaGroup[]).map(item => ({
        label: item.agrupamento,
        value: String(item.grupoArea),
      }));

      setAreaGroupsOptions(options);
    } catch (error) {
      console.error('Erro ao buscar grupos de áreas:', error);
      setAreaGroupsOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id]);

  const fetchAreaSubgroups = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setAreaSubgroupsOptions([]);
      return;
    }

    try {
      const unidadeIdArray = unitId ? [Number(unitId)] : undefined;
      const grupoAreaIdArray = tempAreaGroups.length > 0 ? tempAreaGroups.map(ag => ag.value) : undefined;

      const response = await AgendaService.getAreaSubgroups({
        empresaId: Number(companyId),
        unidadeId: unidadeIdArray,
        grupoAreaId: grupoAreaIdArray,
        companyId: Number(companyId),
        unitId: unitId ? Number(unitId) : undefined,
      });

      if (!response?.data) {
        setAreaSubgroupsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as AreaSubgroup[]).map(item => ({
        label: item.nome,
        value: String(item.subGrupoArea),
      }));

      setAreaSubgroupsOptions(options);
    } catch (error) {
      console.error('Erro ao buscar subgrupos de áreas:', error);
      setAreaSubgroupsOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id, areaGroups, tempAreaGroups]);

  const fetchAreas = useCallback(
    async (page: number = 1, search: string = '', append: boolean = false) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        if (!append) setAreasOptions([]);
        return;
      }

      try {
        const unitsToUse = ambientesEquipamentosModalOpen ? tempMaintenanceUnits : maintenanceUnits;
        const groupsToUse = ambientesEquipamentosModalOpen ? tempAreaGroups : areaGroups;
        const subgroupsToUse = ambientesEquipamentosModalOpen ? tempAreaSubgroups : areaSubgroups;

        const siteIdArray = unitsToUse.length > 0 ? unitsToUse.map(mu => Number(mu.value)) : undefined;
        const grupoAreaIdArray = groupsToUse.length > 0 ? groupsToUse.map(ag => ag.value) : undefined;
        const subGrupoAreaIdArray = subgroupsToUse.length > 0 ? subgroupsToUse.map(asg => asg.value) : undefined;

        const response = await AgendaService.getAreas({
          companyId: Number(companyId),
          siteId: siteIdArray,
          grupoAreaId: grupoAreaIdArray,
          subGrupoAreaId: subGrupoAreaIdArray,
          search: search || undefined,
          pageIndex: page,
          pageSize: 50,
        });

        if (!response?.data) {
          if (!append) setAreasOptions([]);
          return;
        }

        const options: FilterOption[] = (response.data as Area[]).map(item => ({
          label: item.identificacaoAmbiente ? `${item.nome} - ${item.identificacaoAmbiente}` : item.nome,
          value: String(item.area),
        }));

        if (append) {
          setAreasOptions(prev => [...prev, ...options]);
        } else {
          setAreasOptions(options);
        }
      } catch (error) {
        console.error('Erro ao buscar áreas:', error);
        if (!append) setAreasOptions([]);
      }
    },
    [
      headerFilters?.companies?.id,
      ambientesEquipamentosModalOpen,
      maintenanceUnits,
      tempMaintenanceUnits,
      areaGroups,
      tempAreaGroups,
      areaSubgroups,
      tempAreaSubgroups,
    ],
  );

  const loadMoreAreas = useCallback(async () => {
    const nextPage = areaPage + 1;
    await fetchAreas(nextPage, areaSearch, true);
    setAreaPage(nextPage);
  }, [areaPage, areaSearch, fetchAreas]);

  const searchArea = useCallback(
    (term: string) => {
      setAreaSearch(term);
      setAreaPage(1);

      if (debounceAreaRef.current) {
        clearTimeout(debounceAreaRef.current);
      }

      debounceAreaRef.current = setTimeout(async () => {
        await fetchAreas(1, term, false);
      }, 400);
    },
    [fetchAreas],
  );

  const fetchEquipmentTypes = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setEquipmentTypesOptions([]);
      return;
    }

    try {
      const response = await AgendaService.getEquipmentTypes({
        companyId: Number(companyId),
        unitId: Number(unitId),
      });

      if (!response?.data) {
        setEquipmentTypesOptions([]);
        return;
      }

      const filteredData = (response.data as EquipmentType[]).filter(item => !item.inativo);
      equipmentTypesDataRef.current = filteredData;

      const options: FilterOption[] = filteredData.map(item => ({
        label: item.nome,
        value: String(item.linhaProdutoEmpresa),
      }));

      setEquipmentTypesOptions(options);
    } catch (error) {
      console.error('Erro ao buscar tipos de equipamentos:', error);
      setEquipmentTypesOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id]);

  const fetchEquipments = useCallback(
    async (page: number = 1, search: string = '', append: boolean = false) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        if (!append) setEquipmentOptions([]);
        return;
      }

      try {
        const unitsToUse = ambientesEquipamentosModalOpen ? tempMaintenanceUnits : maintenanceUnits;
        const groupsToUse = ambientesEquipamentosModalOpen ? tempAreaGroups : areaGroups;
        const subgroupsToUse = ambientesEquipamentosModalOpen ? tempAreaSubgroups : areaSubgroups;
        const areasToUse = ambientesEquipamentosModalOpen ? tempAreas : areas;
        const equipmentTypesToUse = ambientesEquipamentosModalOpen ? tempEquipmentTypes : equipmentTypes;

        const siteIdArray = unitsToUse.length > 0 ? unitsToUse.map(mu => Number(mu.value)) : undefined;
        const grupoAreaIdArray = groupsToUse.length > 0 ? groupsToUse.map(ag => Number(ag.value)) : undefined;
        const subGrupoAreaIdArray =
          subgroupsToUse.length > 0 ? subgroupsToUse.map(asg => Number(asg.value)) : undefined;
        const areaIdArray = areasToUse.length > 0 ? areasToUse.map(a => a.value) : undefined;
        const tipoEquipamentoIdRaw = equipmentTypesToUse.length > 0 ? Number(equipmentTypesToUse[0].value) : undefined;
        const tipoEquipamentoId =
          tipoEquipamentoIdRaw !== undefined && !Number.isNaN(tipoEquipamentoIdRaw) ? tipoEquipamentoIdRaw : undefined;

        const response = await AgendaService.getEquipments({
          companyId: Number(companyId),
          siteId: siteIdArray,
          grupoAreaId: grupoAreaIdArray,
          subGrupoAreaId: subGrupoAreaIdArray,
          areaId: areaIdArray,
          tipoEquipamentoId: tipoEquipamentoId,
          search: search || undefined,
          pageIndex: page,
          pageSize: 50,
        });

        if (!response?.data) {
          if (!append) setEquipmentOptions([]);
          return;
        }

        const options: FilterOption[] = (response.data as Equipment[]).map(item => ({
          label: item.tag ? `${item.nome} - ${item.tag}` : item.nome,
          value: String(item.equipamento),
        }));

        if (append) {
          setEquipmentOptions(prev => [...prev, ...options]);
        } else {
          setEquipmentOptions(options);
        }
      } catch (error) {
        console.error('Erro ao buscar equipamentos:', error);
        if (!append) setEquipmentOptions([]);
      }
    },
    [
      headerFilters?.companies?.id,
      ambientesEquipamentosModalOpen,
      maintenanceUnits,
      tempMaintenanceUnits,
      areaGroups,
      tempAreaGroups,
      areaSubgroups,
      tempAreaSubgroups,
      areas,
      tempAreas,
      equipmentTypes,
      tempEquipmentTypes,
    ],
  );

  const loadMoreEquipamentos = useCallback(async () => {
    const nextPage = equipamentoPage + 1;
    await fetchEquipments(nextPage, equipamentoSearch, true);
    setEquipamentoPage(nextPage);
  }, [equipamentoPage, equipamentoSearch, fetchEquipments]);

  const searchEquipamento = useCallback(
    (term: string) => {
      setEquipamentoSearch(term);
      setEquipamentoPage(1);

      if (debounceEquipamentoRef.current) {
        clearTimeout(debounceEquipamentoRef.current);
      }

      debounceEquipamentoRef.current = setTimeout(async () => {
        await fetchEquipments(1, term, false);
      }, 400);
    },
    [fetchEquipments],
  );

  useEffect(() => {
    if (!executoresSetoresModalOpen) {
      return;
    }
    fetchSetores();
    fetchExecutores();
  }, [executoresSetoresModalOpen, headerFilters?.companies?.id, headerFilters?.units?.id]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen && !planosModalOpen) {
      return;
    }

    fetchMaintenanceUnits();
  }, [
    ambientesEquipamentosModalOpen,
    planosModalOpen,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    maintenanceUnits,
  ]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreaGroups();
  }, [ambientesEquipamentosModalOpen, headerFilters?.companies?.id, headerFilters?.units?.id]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreaSubgroups();
  }, [
    ambientesEquipamentosModalOpen,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    areaGroups,
    tempAreaGroups,
  ]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreas();
  }, [ambientesEquipamentosModalOpen, headerFilters?.companies?.id, maintenanceUnits, areaGroups, areaSubgroups]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchEquipmentTypes();
  }, [ambientesEquipamentosModalOpen, headerFilters?.companies?.id, headerFilters?.units?.id]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    setEquipamentoPage(1);
    setEquipamentoSearch('');
    fetchEquipments(1, '', false);
  }, [
    ambientesEquipamentosModalOpen,
    headerFilters?.companies?.id,
    maintenanceUnits,
    areaGroups,
    areaSubgroups,
    areas,
    equipmentTypes,
    fetchEquipments,
  ]);

  const setWeekView = useCallback((isWeekView: boolean) => {
    isWeekViewRef.current = isWeekView;
  }, []);

  const fetchAgendaTasks = useCallback(
    async (isWeekView?: boolean, overrides?: { planosValues?: string[] }) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId || !effectiveDate) {
        if (isMountedRef.current) setEvents({});
        return;
      }

      if (isMountedRef.current) setIsLoadingEvents(true);

      try {
        const baixadas = type === 'baixadas';
        let usuarioIdNumber: number | undefined;

        if (authContext?.token) {
          const { nameid } = parseJwt(authContext.token);
          if (nameid) {
            const parsedUserId = Number(nameid);
            if (!Number.isNaN(parsedUserId)) {
              usuarioIdNumber = parsedUserId;
            }
          }
        }

        const isWeek =
          isWeekView !== undefined
            ? isWeekView
            : view !== undefined
            ? view === CalendarView.Semana
            : isWeekViewRef.current;
        const dateToUse = isWeek ? startOfWeek(effectiveDate, { weekStartsOn: 0 }) : effectiveDate;
        const selectedDate = dateToUse.toISOString();

        const shouldAddDays = isWeek;

        const baseParams: Parameters<typeof AgendaService.getAgendaTasks>[0] = {
          SelectedDate: selectedDate,
          Baixadas: baixadas,
          EmpresaId: Number(companyId),
          ...(usuarioIdNumber !== undefined && { UsuarioId: usuarioIdNumber }),
          ...(headerFilters?.providers?.id && { FornecedorId: Number(headerFilters.providers.id) }),
          ...(shouldAddDays && { DiasAdicionais: 6 }),
        };

        const effectivePlanosValues = overrides?.planosValues ?? planosValues;

        const apiParams = buildApiPayload(baseParams, {
          unitGroupValues: {
            values: globalUnitGroupValues,
            apiFieldName: 'GrupoUnidadeId',
            alwaysArray: false,
          },
          unitSubgroupValues: {
            values: globalUnitSubgroupValues,
            apiFieldName: 'SubGrupoUnidadeId',
            alwaysArray: false,
          },
          stateValues: {
            values: globalStateValues,
            apiFieldName: 'EstadoId',
            alwaysArray: false,
          },
          cityValues: {
            values: globalCityValues,
            apiFieldName: 'CidadeId',
            alwaysArray: false,
          },
          systemValues: {
            values: globalSystemValues,
            apiFieldName: 'SistemaEmpresaId',
            alwaysArray: false,
          },
          maintenanceUnitsValues: {
            values: allMaintenanceUnitsValues,
            apiFieldName: 'SiteId',
            alwaysArray: true,
          },
          planosValues: {
            values: effectivePlanosValues,
            apiFieldName: 'PlanoAtividadeId',
            alwaysArray: false,
          },
          executoresValues: {
            values: executoresValues,
            apiFieldName: 'ExecutorId',
            alwaysArray: false,
          },
          setoresValues: {
            values: setoresValues,
            apiFieldName: 'SetorId',
            alwaysArray: false,
          },
          areaGroupsValues: {
            values: areaGroupsValues,
            apiFieldName: 'GrupoAreaId',
            alwaysArray: false,
          },
          areaSubgroupsValues: {
            values: areaSubgroupsValues,
            apiFieldName: 'SubGrupoAreaId',
            alwaysArray: false,
          },
          areasValues: {
            values: areasValues,
            apiFieldName: 'AreaId',
            alwaysArray: false,
          },
          equipmentTypesValues: {
            values: equipmentTypesValues,
            apiFieldName: 'TipoEquipamentoId',
            alwaysArray: false,
          },
          equipmentValues: {
            values: equipmentValues,
            apiFieldName: 'EquipamentoId',
            alwaysArray: false,
          },
        });

        const response = await AgendaService.getAgendaTasks(apiParams);

        if (isMountedRef.current && response?.data && Array.isArray(response.data)) {
          const transformedEvents = transformAgendaTasksToEvents(response.data as AgendaTask[]);
          setEvents(transformedEvents);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Erro ao buscar tarefas da agenda:', error);
          setEvents({});
        }
      } finally {
        if (isMountedRef.current) setIsLoadingEvents(false);
      }
    },
    [
      type,
      view,
      planosValues,
      executoresValues,
      setoresValues,
      allMaintenanceUnitsValues,
      areaGroupsValues,
      areaSubgroupsValues,
      areasValues,
      equipmentTypesValues,
      equipmentValues,
      headerFilters?.companies?.id,
      headerFilters?.units?.id,
      globalUnitGroupValues,
      globalUnitSubgroupValues,
      globalStateValues,
      globalCityValues,
      effectiveDate,
    ],
  );

  useEffect(() => {
    const isWeekView = view === CalendarView.Semana;
    isWeekViewRef.current = isWeekView;
    fetchAgendaTasks(isWeekView);
  }, [view]);

  useEffect(() => {
    if (planosModalOpen || ambientesEquipamentosModalOpen) {
      return;
    }

    const fetchKey = JSON.stringify({
      currentDateString,
      type,
      planosValues,
      executoresValues,
      setoresValues,
      maintenanceUnitsValues,
      areaGroupsValues,
      areaSubgroupsValues,
      areasValues,
      equipmentTypesValues,
      equipmentValues,
      companyId: headerFilters?.companies?.id,
      unitId: headerFilters?.units?.id,
      unitGroupId: globalUnitGroupValues.length > 0 ? globalUnitGroupValues[0] : null,
      unitSubgroupId: globalUnitSubgroupValues.length > 0 ? globalUnitSubgroupValues[0] : null,
      stateId: globalStateValues.length > 0 ? globalStateValues[0] : null,
      cityId: globalCityValues.length > 0 ? globalCityValues[0] : null,
      isWeekView: isWeekViewRef.current,
    });

    if (lastFetchParamsRef.current === fetchKey) {
      return;
    }

    lastFetchParamsRef.current = fetchKey;
    const isWeekView = view === undefined ? true : view === CalendarView.Semana;
    fetchAgendaTasks(isWeekView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planosModalOpen,
    ambientesEquipamentosModalOpen,
    view,
    currentDateString,
    type,
    planosValues,
    executoresValues,
    setoresValues,
    maintenanceUnitsValues,
    areaGroupsValues,
    areaSubgroupsValues,
    areasValues,
    equipmentTypesValues,
    equipmentValues,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    globalUnitGroupValues,
    globalUnitSubgroupValues,
    globalStateValues,
    globalCityValues,
    globalSystemValues,
  ]);

  useEffect(() => {
    if (executoresSetoresModalOpen) {
      setTempExecutores(executores || []);
      setTempSetores(setores || []);
    }
  }, [executoresSetoresModalOpen, executores, setores]);

  useEffect(() => {
    if (planosModalOpen) {
      setTempPlanos(planos || []);
      if (appliedMaintenanceUnitsForPlanos.length > 0) {
        setTempMaintenanceUnitsForPlanos(appliedMaintenanceUnitsForPlanos);
      } else {
        const globalUnitId = headerFilters?.units?.id;
        if (globalUnitId && maintenanceUnitsOptions.length > 0) {
          const globalUnitOption = maintenanceUnitsOptions.find(opt => opt.value === String(globalUnitId));
          if (globalUnitOption) {
            setTempMaintenanceUnitsForPlanos([globalUnitOption]);
          } else {
            setTempMaintenanceUnitsForPlanos([]);
          }
        } else {
          setTempMaintenanceUnitsForPlanos([]);
        }
      }
    }
  }, [planosModalOpen, planos, appliedMaintenanceUnitsForPlanos, headerFilters?.units?.id, maintenanceUnitsOptions]);

  useEffect(() => {
    if (planosModalOpen && tempMaintenanceUnitsForPlanos.length > 0) {
      fetchActivitiesPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planosModalOpen, tempMaintenanceUnitsForPlanos]);

  useEffect(() => {
    if (ambientesEquipamentosModalOpen) {
      setTempMaintenanceUnits(maintenanceUnits || []);
      setTempAreaGroups(areaGroups || []);
      setTempAreaSubgroups(areaSubgroups || []);
      setTempAreas(areas || []);
      setTempEquipmentTypes(equipmentTypes || []);
      setTempEquipment(equipment || []);
    }
  }, [ambientesEquipamentosModalOpen, maintenanceUnits, areaGroups, areaSubgroups, areas, equipmentTypes, equipment]);

  return {
    type,
    setType,
    planos,
    setPlanos,
    planosSelectionLabel,
    planosValues,
    handlePlanosChange,
    planosOptions,
    ocorrencias,
    setOcorrencias,
    executores,
    setExecutores,
    executoresValues,
    handleExecutoresChange,
    executoresOptions,
    tempExecutoresValues,
    handleTempExecutoresChange,
    setores,
    setSetores,
    setoresValues,
    handleSetoresChange,
    setoresOptions,
    tempSetoresValues,
    handleTempSetoresChange,
    applyExecutoresSetoresFilters,
    clearExecutoresSetoresFilters,
    fields,
    openFilters,
    setOpenFilters,
    executoresSetoresModalOpen,
    setExecutoresSetoresModalOpen,
    ambientesEquipamentosModalOpen,
    setAmbientesEquipamentosModalOpen,
    planosModalOpen,
    setPlanosModalOpen,
    tempPlanosValues,
    handleTempPlanosChange,
    tempMaintenanceUnitsForPlanosValues,
    handleTempMaintenanceUnitsForPlanosChange,
    applyPlanosFilters,
    clearPlanosFilters,
    maintenanceUnitsOptions,
    maintenanceUnitsValues,
    handleMaintenanceUnitsChange,
    tempMaintenanceUnitsValues,
    handleTempMaintenanceUnitsChange,
    areaGroupsOptions,
    areaGroupsValues,
    handleAreaGroupsChange,
    tempAreaGroupsValues,
    handleTempAreaGroupsChange,
    areaSubgroupsOptions,
    areaSubgroupsValues,
    handleAreaSubgroupsChange,
    tempAreaSubgroupsValues,
    handleTempAreaSubgroupsChange,
    areasOptions,
    areasValues,
    handleAreasChange,
    tempAreasValues,
    handleTempAreasChange,
    equipmentTypesOptions,
    equipmentTypesValues,
    handleEquipmentTypesChange,
    tempEquipmentTypesValues,
    handleTempEquipmentTypesChange,
    equipmentOptions,
    equipmentValues,
    handleEquipmentChange,
    tempEquipmentValues,
    handleTempEquipmentChange,
    applyAmbientesEquipamentosFilters,
    clearAmbientesEquipamentosFilters,
    hasActiveFilters,
    clearAllFilters,
    events,
    isLoadingEvents,
    fetchAgendaTasks,
    setWeekView,
    loadMoreAreas,
    searchArea,
    loadMoreEquipamentos,
    searchEquipamento,
  };
};
